/**
 * MurialAndCo standalone configurator page (US market).
 * URL: /pages/configurateur?product=HANDLE&material=premium
 *
 * Units: inches throughout the UI. Area is computed in square feet for pricing.
 *
 * Flow:
 * 1. Read product handle + material from URL
 * 2. Fetch product data via Section Rendering API (includes metafields)
 * 3. Show wallpaper in Cropper.js
 * 4. On "Order": call MurialAndCo API inline, add to cart, redirect to /cart
 *    (no intermediate loading page)
 */

(function () {
  'use strict';

  // Minimum billable area: 1 m2 = 10.7639104 sq ft. Dimensions stay free, but a
  // smaller panel is billed as that. Duplicated in api/index.php ($aire_min_sqft)
  // - change both or the customer pays a price they never saw.
  var AIRE_MIN_SQFT = 10.7639104;

  var PAPER_TYPES = {
    'premium': { label: 'Premium Matte',      desc: 'Tear-Proof · Humidity-Resistant · Velvety Matte',    surcharge: 0, price: 4 },
    'adhesif': { label: 'Peel & Stick Vinyl', desc: 'Renter-Approved · Humidity-Resistant · Tear-Proof', surcharge: 2, price: 6 }
  };
  // Map old keys from product page URL to new keys
  var KEY_ALIASES = { 'rebel-mattic': 'premium', 'peel-stick': 'adhesif' };

  var API_URL = 'https://api.muralandco.com/';

  // URL params
  var params = new URLSearchParams(window.location.search);
  var productHandle = params.get('product');
  var rawMaterial = params.get('material') || 'premium';
  var initialMaterial = KEY_ALIASES[rawMaterial] || rawMaterial;

  if (!productHandle) {
    document.getElementById('configurator-app').innerHTML = '<p style="text-align:center;padding:60px 20px;color:#6B6B6B;">No product selected. <a href="/" style="color:#2B2B2B;text-decoration:underline;">Back</a></p>';
    return;
  }

  // State
  var product = null;
  var basePrice = 0;
  var productTitle = '';
  var commercialName = '';
  var designPapierUrl = '';
  var productId = '';
  var productReference = '';
  var cropperInstance = null;
  var selectedMaterial = initialMaterial;

  // DOM refs. Width/Height are each split into a feet input and an inch input
  // (US-style "10 ft 6 in"). All internal math uses total inches.
  var elImage, elWidthFt, elWidthIn, elHeightFt, elHeightIn, elPrice, elButton, elError, elTopBar, elRightBar, elBox, elRowMain, elSection, elTable, elPanels, elPanelsBtn;

  function getWidthInches() {
    var ft = parseFloat(elWidthFt && elWidthFt.value) || 0;
    var inc = parseFloat(elWidthIn && elWidthIn.value) || 0;
    return ft * 12 + inc;
  }
  function getHeightInches() {
    var ft = parseFloat(elHeightFt && elHeightFt.value) || 0;
    var inc = parseFloat(elHeightIn && elHeightIn.value) || 0;
    return ft * 12 + inc;
  }
  // "126" → "10' 6\""
  function formatDimDisplay(totalInches) {
    var ft = Math.floor(totalInches / 12);
    var inc = Math.round(totalInches - ft * 12);
    if (inc === 12) { ft += 1; inc = 0; }
    return ft + "' " + inc + '"';
  }
  var panelsOn = false;
  // Standard US wallpaper roll width is 20.5". Use 20 in for cleaner panel preview.
  var PANEL_WIDTH_IN = 20.5;

  // --- Load product data via Section Rendering API ---
  async function loadProduct() {
    try {
      // Fetch section that has product context (resolves metafields)
      var sectionResp = await fetch('/products/' + productHandle + '?section_id=configurator-meta');
      if (!sectionResp.ok) throw new Error('Section fetch failed');
      var sectionHtml = await sectionResp.text();

      // Parse the HTML to extract data attributes
      var parser = new DOMParser();
      var doc = parser.parseFromString(sectionHtml, 'text/html');
      var meta = doc.getElementById('cfg-meta-output');

      if (meta) {
        designPapierUrl = meta.dataset.designPapier || '';
        productId = meta.dataset.productId || '';
        basePrice = parseFloat(meta.dataset.basePrice || '0') / 100; // Shopify prices in cents
        productTitle = meta.dataset.productTitle || '';
        commercialName = meta.dataset.commercialName || '';
      }

      // Also fetch basic product data for images fallback
      var prodResp = await fetch('/products/' + productHandle + '.json');
      if (prodResp.ok) {
        var prodData = await prodResp.json();
        product = prodData.product;
        if (!productTitle) productTitle = product.title;
        if (!productId) productId = product.id;
        if (!basePrice && product.variants && product.variants.length) {
          basePrice = parseFloat(product.variants[0].price);
        }
        // Extract MC reference (format: MC{YY}{#####}) from tags
        var tags = product.tags || [];
        if (typeof tags === 'string') tags = tags.split(',').map(function(t){ return t.trim(); });
        for (var i = 0; i < tags.length; i++) {
          if (/^MC\d{2}\d{5}$/.test(tags[i])) { productReference = tags[i]; break; }
        }
      }

      renderConfigurator();
      initCropper();
    } catch (e) {
      document.getElementById('configurator-app').innerHTML = '<p style="text-align:center;padding:60px 20px;color:#b00020;">Product not found: ' + productHandle + '</p>';
      console.error(e);
    }
  }

  // --- Render ---
  function renderConfigurator() {
    var imageUrl = designPapierUrl;
    if (!imageUrl && product && product.images && product.images.length) {
      imageUrl = product.images[0].src;
    }
    if (!imageUrl) {
      imageUrl = 'https://placehold.co/1200x800/f7f5f2/ccc?text=No+image';
    }

    var paperBtns = '';
    for (var key in PAPER_TYPES) {
      var type = PAPER_TYPES[key];
      var active = key === selectedMaterial ? ' active' : '';
      paperBtns += '<label class="cfg-paper-option' + active + '">'
        + '<input type="radio" name="paper-type" value="' + key + '" data-surcharge="' + type.surcharge + '" data-label="' + type.label + '"' + (active ? ' checked' : '') + '>'
        + '<div class="cfg-paper-row"><strong>' + type.label + '</strong><span class="cfg-paper-price">$' + type.price + '/sq ft</span></div>'
        + '<span class="cfg-paper-desc">' + type.desc + '</span>'
        + '</label>';
    }

    var displayName = commercialName || productTitle || 'Wallpaper';

    var app = document.getElementById('configurator-app');
    app.innerHTML = ''
      // Header: floating circular back button + centered title
      + '<a href="/products/' + productHandle + '" class="cfg-back" aria-label="Back to product">'
      + '  <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M15 6l-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      + '</a>'
      + '<div class="cfg-header">'
      + '  <button type="button" id="cfg-panels-toggle" class="cfg-panels-pill" aria-pressed="false">'
      + '    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true"><path d="M6 4v16M12 4v16M18 4v16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>'
      + '    <span>Show panels</span>'
      + '  </button>'
      + '</div>'
      // Cropper: container fills the section; crop box is a sub-rectangle with
      // padding; bars are absolute overlays anchored to the crop box edges.
      + '<div class="cfg-cropper-section">'
      + '  <div class="cfg-cropper-box"><img id="cfg-image" src="' + imageUrl + '" crossorigin="anonymous" alt="' + displayName + '"></div>'
      + '  <div id="cfg-top-bar" class="cfg-bar-h"></div>'
      + '  <div id="cfg-right-bar" class="cfg-bar-v"></div>'
      + '  <div id="cfg-panels" class="cfg-panels" aria-hidden="true"></div>'
      + '</div>'
      // Hint sits above the cards, out of the cropper/image area
      + '<div class="cfg-crop-hint">'
      + '  <svg viewBox="0 0 24 24" width="13" height="13" aria-hidden="true"><path d="M12 3v18M3 12h18M12 3l-3 3M12 3l3 3M12 21l-3-3M12 21l3-3M3 12l3-3M3 12l3 3M21 12l-3-3M21 12l-3 3" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      + '  <span>Drag to reposition</span>'
      + '</div>'
      // Horizontal controls bar
      + '<div class="cfg-controls">'
      + '  <div class="cfg-ctrl-section">'
      + '    <div class="cfg-ctrl-label">Wall dimensions</div>'
      + '    <div class="cfg-size-inputs">'
      + '      <div class="cfg-input-group">'
      + '        <label><svg viewBox="0 0 24 24" width="11" height="11" aria-hidden="true" style="vertical-align:-1px;margin-right:4px;"><path d="M3 12h18M3 12l4-4M3 12l4 4M21 12l-4-4M21 12l-4 4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>Width</label>'
      + '        <div class="cfg-input-wrap"><input type="number" id="cfg-width-ft" value="10" min="1" max="20" step="1"><span class="cfg-unit">ft</span><input type="number" id="cfg-width-in" value="0" min="0" max="11" step="1"><span class="cfg-unit">in</span></div>'
      + '      </div>'
      + '      <div class="cfg-input-group">'
      + '        <label><svg viewBox="0 0 24 24" width="11" height="11" aria-hidden="true" style="vertical-align:-1px;margin-right:4px;"><path d="M12 3v18M12 3l-4 4M12 3l4 4M12 21l-4-4M12 21l4-4" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>Height</label>'
      + '        <div class="cfg-input-wrap"><input type="number" id="cfg-height-ft" value="8" min="1" max="13" step="1"><span class="cfg-unit">ft</span><input type="number" id="cfg-height-in" value="4" min="0" max="11" step="1"><span class="cfg-unit">in</span></div>'
      + '      </div>'
      + '    </div>'
      + '    <div class="cfg-size-note">Add 3–4 inches of extra margin to your measurements for a safe install.</div>'
      + '    <button type="button" id="cfg-flip" class="cfg-pill-btn" aria-pressed="false">'
      + '      <svg viewBox="0 0 24 24" width="14" height="14" aria-hidden="true"><path d="M4 7h7v10H4zM13 7h7v10h-7zM13 4v3M13 17v3M13 10h2M13 12h2M13 14h2" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>'
      + '      <span>Mirror</span>'
      + '    </button>'
      + '  </div>'
      + '  <div class="cfg-ctrl-section">'
      + '    <div class="cfg-ctrl-label">Wallpaper type</div>'
      + '    <div class="cfg-paper-types">' + paperBtns + '</div>'
      + '  </div>'
      + '  <div class="cfg-ctrl-section cfg-cta-section">'
      + '    <div class="cfg-total-row">'
      + '      <span class="cfg-total-label" id="cfg-total-label">Price</span>'
      + '      <span class="cfg-total-price" id="cfg-price">--</span>'
      + '    </div>'
      + '    <div id="cfg-error" class="cfg-error"></div>'
      + '    <button type="button" id="cfg-order-btn" class="button button-solid add_to_cart_button w-full cfg-order-btn" disabled>'
      + '      <span class="flex justify-center items-center">'
      + '        <span class="x-atc-text button-text" id="cfg-btn-text">Confirm my customization</span>'
      + '        <span class="button-icon opacity-0 w-5 -translate-x-[4px]">'
      + '          <svg xmlns="http://www.w3.org/2000/svg" width="19" height="19" viewBox="0 0 19 19" fill="none" style="fill:none" aria-hidden="true">'
      + '            <path d="M4.25 9.49927H14.75" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'
      + '            <path d="M9.5 4.24927L14.75 9.49927L9.5 14.7493" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>'
      + '          </svg>'
      + '        </span>'
      + '      </span>'
      + '      <span id="cfg-btn-spinner" style="display:none;" class="cfg-spinner"></span>'
      + '    </button>'
      + '    <div id="cfg-payments-slot"></div>'
      + '    <ul class="cfg-reassurance">'
      + '      <li id="cfg-delivery"></li>'
      + '      <li>Free shipping to your door</li>'
      + '      <li>Peace-of-mind guarantee included</li>'
      + '    </ul>'
      + '  </div>'
      + '</div>';

    // Bind refs
    elImage = document.getElementById('cfg-image');
    elWidthFt = document.getElementById('cfg-width-ft');
    elWidthIn = document.getElementById('cfg-width-in');
    elHeightFt = document.getElementById('cfg-height-ft');
    elHeightIn = document.getElementById('cfg-height-in');
    elPrice = document.getElementById('cfg-price');
    elButton = document.getElementById('cfg-order-btn');
    elError = document.getElementById('cfg-error');
    elTopBar = document.getElementById('cfg-top-bar');
    elRightBar = document.getElementById('cfg-right-bar');
    elBox = document.querySelector('.cfg-cropper-box');
    elRowMain = document.querySelector('.cfg-row-main');
    elSection = document.querySelector('.cfg-cropper-section');
    elTable = document.querySelector('.cfg-cropper-table');
    elPanels = document.getElementById('cfg-panels');
    elPanelsBtn = document.getElementById('cfg-panels-toggle');

    // Events — debounce the cropper rebuild so typing "10" doesn't flash the
    // image twice while still keeping the on-screen $ price feedback instant.
    function onDimInput() {
      updatePriceOnly();
      schedulePriceRebuild();
    }
    elWidthFt.addEventListener('input', onDimInput);
    elWidthIn.addEventListener('input', onDimInput);
    elHeightFt.addEventListener('input', onDimInput);
    elHeightIn.addEventListener('input', onDimInput);
    document.querySelectorAll('input[name="paper-type"]').forEach(function (el) {
      el.addEventListener('change', function () {
        selectedMaterial = this.value;
        document.querySelectorAll('.cfg-paper-option').forEach(function (o) { o.classList.remove('active'); });
        this.closest('.cfg-paper-option').classList.add('active');
        updatePriceOnly();
      });
    });
    var flipBtn = document.getElementById('cfg-flip');
    flipBtn.addEventListener('click', function () {
      var on = flipBtn.getAttribute('aria-pressed') === 'true';
      var next = !on;
      flipBtn.setAttribute('aria-pressed', next ? 'true' : 'false');
      flipBtn.classList.toggle('active', next);
      if (cropperInstance) cropperInstance.scaleX(next ? -1 : 1);
    });
    elButton.addEventListener('click', handleOrder);

    elPanelsBtn.addEventListener('click', function () {
      panelsOn = !panelsOn;
      elPanelsBtn.setAttribute('aria-pressed', panelsOn ? 'true' : 'false');
      elPanelsBtn.classList.toggle('active', panelsOn);
      elPanels.setAttribute('aria-hidden', panelsOn ? 'false' : 'true');
      updatePanels();
    });

    // Inject the server-rendered Shopify payment icons (see page.configurateur.json).
    var paymentsTpl = document.getElementById('cfg-payments-template');
    var paymentsSlot = document.getElementById('cfg-payments-slot');
    if (paymentsTpl && paymentsSlot) {
      paymentsSlot.innerHTML = paymentsTpl.innerHTML;
    }

    updateDeliveryText();
  }

  // --- Cropper ---
  function buildCropper(onReady) {
    cropperInstance = new Cropper(elImage, {
      autoCropArea: 1,
      viewMode: 1,            // crop box stays inside canvas → natural axis-lock on the filled axis
      movable: true,          // image can be dragged
      zoomable: false,
      zoomOnTouch: false,
      zoomOnWheel: false,
      restore: false,
      rotatable: false,
      scalable: true,
      dragMode: 'move',       // drag moves the image behind the crop box
      modal: true,
      responsive: false,      // we drive reflow ourselves (debounced) on resize
      cropBoxResizable: false,
      cropBoxMovable: false,  // crop box stays FIXED
      ready: function () {
        if (typeof onReady === 'function') onReady();
      }
    });
  }

  function initCropper() {
    function boot() {
      if (typeof Cropper === 'undefined') { setTimeout(boot, 100); return; }
      fitBoxToRatio();
      buildCropper(function () {
        applyCropBoxFromDims();
        updateBars();
        elButton.disabled = false;
        updatePriceOnly();
      });
    }
    boot();
  }

  // Stub — container now fills the section; no box resizing is needed.
  function fitBoxToRatio() { /* no-op */ }

  // Pin the crop box to one full axis of the container so drag is naturally
  // locked to the other axis by viewMode:1:
  //   - container wider than wall → crop box fills full height → horizontal drag only
  //   - container taller than wall → crop box fills full width → vertical drag only
  // Bar strips (22px top, 24px right) are reserved via CSS padding on cropper-box
  // so the cropper's container *is* the exact drag surface — no slack along the
  // pinned axis. Dimmed image remains visible on the unpinned sides.
  function applyCropBoxFromDims() {
    if (!cropperInstance) return;
    var w = getWidthInches(), h = getHeightInches();
    if (isNaN(w) || isNaN(h) || w <= 0 || h <= 0) return;
    cropperInstance.setAspectRatio(w / h);
    var cd = cropperInstance.getContainerData();
    if (cd.width <= 0 || cd.height <= 0) return;

    var ratio = w / h;
    var boxW, boxH, left, top;
    if (cd.width / cd.height > ratio) {
      boxH = cd.height;
      boxW = boxH * ratio;
      left = (cd.width - boxW) / 2;
      top = 0;
    } else {
      boxW = cd.width;
      boxH = boxW / ratio;
      left = 0;
      top = (cd.height - boxH) / 2;
    }
    cropperInstance.setCropBoxData({ left: left, top: top, width: boxW, height: boxH });
  }

  function updateBars() {
    if (!cropperInstance || !elTopBar || !elRightBar || !elBox) return;
    var cbd = cropperInstance.getCropBoxData();
    // cbd is relative to the cropper container (= cropper-box). Translate into
    // section coords by adding cropper-box's own offset inside the section.
    var boxLeft = elBox.offsetLeft; // 0 (CSS left: 0)
    var boxTop = elBox.offsetTop;   // 22 (CSS top: 22px)
    var BAR_H = 22;
    elTopBar.style.left = (cbd.left + boxLeft) + 'px';
    elTopBar.style.top = (cbd.top + boxTop - BAR_H) + 'px';
    elTopBar.style.width = cbd.width + 'px';
    elTopBar.textContent = formatDimDisplay(getWidthInches());
    elRightBar.style.left = (cbd.left + cbd.width + boxLeft) + 'px';
    elRightBar.style.top = (cbd.top + boxTop) + 'px';
    elRightBar.style.height = cbd.height + 'px';
    elRightBar.innerHTML = '<div class="cfg-bar-v-text">' + formatDimDisplay(getHeightInches()) + '</div>';
    updatePanels();
  }

  // Render vertical lines every PANEL_WIDTH_IN (20.5") inside the crop box,
  // starting from its left edge, to preview the wallpaper strips (panels).
  function updatePanels() {
    if (!elPanels || !cropperInstance) return;
    elPanels.innerHTML = '';
    if (!panelsOn) { elPanels.style.display = 'none'; return; }
    var w = getWidthInches();
    if (w <= 0) { elPanels.style.display = 'none'; return; }
    var cbd = cropperInstance.getCropBoxData();
    var boxLeft = elBox.offsetLeft;
    var boxTop = elBox.offsetTop;
    elPanels.style.display = 'block';
    elPanels.style.left = (cbd.left + boxLeft) + 'px';
    elPanels.style.top = (cbd.top + boxTop) + 'px';
    elPanels.style.width = cbd.width + 'px';
    elPanels.style.height = cbd.height + 'px';
    var pxPerIn = cbd.width / w;
    var nPanels = Math.ceil(w / PANEL_WIDTH_IN);
    for (var i = 1; i < nPanels; i++) {
      var x = i * PANEL_WIDTH_IN * pxPerIn;
      if (x >= cbd.width) break;
      var line = document.createElement('div');
      line.className = 'cfg-panel-line';
      line.style.left = x + 'px';
      elPanels.appendChild(line);
    }
  }

  // --- Price ---
  function getSelectedPaper() {
    var el = document.querySelector('input[name="paper-type"]:checked');
    if (!el) return PAPER_TYPES['premium'];
    return PAPER_TYPES[el.value] || PAPER_TYPES['premium'];
  }

  // Debounce the destroy+rebuild on dimension changes. Without this every
  // keystroke (typing "10" → "1", then "10") triggers a full Cropper rebuild,
  // showing the raw <img> for a few hundred ms between destroy and ready.
  var rebuildTimer = null;
  var REBUILD_DEBOUNCE_MS = 280;
  function schedulePriceRebuild() {
    clearTimeout(rebuildTimer);
    rebuildTimer = setTimeout(updatePrice, REBUILD_DEBOUNCE_MS);
  }

  function updatePrice() {
    if (!cropperInstance) return;
    var w = getWidthInches(), h = getHeightInches();
    if (w <= 0 || h <= 0) return;

    // Preserve mirror state across rebuild
    var mirrored = false;
    try { mirrored = cropperInstance.getData().scaleX < 0; } catch (e) {}

    // Resize the cropper box to the new aspect ratio, then rebuild the cropper
    // so its canvas fills the new container perfectly (no whitespace gap).
    fitBoxToRatio();
    cropperInstance.destroy();
    cropperInstance = null;

    buildCropper(function () {
      applyCropBoxFromDims();
      if (mirrored) cropperInstance.scaleX(-1);
      updateBars();
    });

    updatePriceOnly();
  }

  function updatePriceOnly() {
    var w = getWidthInches(), h = getHeightInches();
    var paper = getSelectedPaper();
    var currentBase = basePrice + paper.surcharge; // base $ per sq ft, including paper-type surcharge
    if (w > 0 && h > 0) {
      var area = (w / 12) * (h / 12); // sq ft
      var aireFacturee = Math.max(AIRE_MIN_SQFT, area);
      elPrice.textContent = '$' + (currentBase * aireFacturee).toFixed(2);
      var label = document.getElementById('cfg-total-label');
      if (label) label.textContent = 'Price for ' + aireFacturee.toFixed(2) + ' sq ft'
        + (aireFacturee > area ? ' (minimum billed)' : '');
    }
  }

  // Delivery window text: today + 4 to today + 5, skipping Sundays.
  // Example: "Order today, get it between 04/24 and 04/26"
  function addBusinessDays(date, days) {
    var d = new Date(date.getTime());
    var added = 0;
    while (added < days) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() !== 0) added++; // skip Sundays
    }
    return d;
  }
  function formatDM(d) {
    // US date format: MM/DD
    var mm = String(d.getMonth() + 1).padStart(2, '0');
    var dd = String(d.getDate()).padStart(2, '0');
    return mm + '/' + dd;
  }
  function updateDeliveryText() {
    var el = document.getElementById('cfg-delivery');
    if (!el) return;
    var today = new Date();
    var from = addBusinessDays(today, 4);
    var to = addBusinessDays(today, 5);
    el.textContent = 'All US tariffs & customs fees included';
  }

  // --- Order: inline API call + add to cart (NO loading page) ---
  async function handleOrder() {
    elError.textContent = '';
    elError.style.display = 'none';

    var w = getWidthInches(), h = getHeightInches();
    if (!w || !h) { showError('Please enter dimensions.'); return; }
    if (w < 20 || w > 240) { showError('Width must be between 1 ft 8 in and 20 ft.'); return; }
    if (h < 20 || h > 160) { showError('Height must be between 1 ft 8 in and 13 ft 4 in.'); return; }
    if (!cropperInstance) { showError('Configurator not ready yet.'); return; }

    var cropData = cropperInstance.getData();
    var reverseMode = document.getElementById('cfg-flip').getAttribute('aria-pressed') === 'true';
    var paper = getSelectedPaper();
    var materialKey = (document.querySelector('input[name="paper-type"]:checked') || {}).value || 'premium';

    // Show spinner
    setButtonLoading(true);

    try {
      // Build FormData for API. Width/height sent in INCHES; API converts as needed.
      var fd = new FormData();
      fd.append('origin_url', elImage.src);
      fd.append('product_id', productId);
      fd.append('width', w);
      fd.append('height', h);
      fd.append('unit', 'in');
      fd.append('paper_type', materialKey);
      fd.append('reverse_mode', reverseMode);
      fd.append('crop_box[x]', cropData.x);
      fd.append('crop_box[y]', cropData.y);
      fd.append('crop_box[width]', cropData.width);
      fd.append('crop_box[height]', cropData.height);

      // Call API
      var resp = await fetch(API_URL, { method: 'POST', body: fd });
      var result = await resp.json();

      if (result.status !== 'success') {
        // Retry once
        fd.append('retry', 'true');
        resp = await fetch(API_URL, { method: 'POST', body: fd });
        result = await resp.json();
      }

      if (result.status !== 'success') {
        throw new Error(result.message || 'Error creating your wallpaper.');
      }

      // Build cart properties
      var props = {
        'Reference': productReference,
        'Paper type': paper.label,
        'Print type': 'custom',
        'Width': formatDimDisplay(w),
        'Height': formatDimDisplay(h) + (reverseMode ? ' (mirror)' : ''),
        'Preview': result.image,
        '_product_title': productTitle,
        '_product_price': result.product_price,
        // Cart template (main-cart-items.liquid, cart-drawer.liquid) reads
        // _product_image and renders it as the line thumbnail instead of
        // the variant's Shopify image — gives the customer a visual of their
        // actual crop in the cart.
        '_product_image': result.image
      };

      // Wait for Shopify to propagate the new variant to the storefront
      await new Promise(function(r) { setTimeout(r, 2500); });

      // Add to cart with retry backoff for the "is already sold out" race —
      // Shopify can take a few seconds after variant creation before the
      // storefront cart API recognises the variant as available.
      var cartBody = JSON.stringify({ id: result.product_id, quantity: 1, properties: props });
      var cartHeaders = { 'Content-Type': 'application/json', 'Accept': 'application/json' };
      var cartResp = null;
      var cartErr = null;
      var soldOutCleared = false;
      var attempts = [0, 1500, 2500, 4000, 6000]; // ms to wait BEFORE each attempt

      for (var a = 0; a < attempts.length; a++) {
        if (attempts[a] > 0) {
          await new Promise(function(r) { setTimeout(r, attempts[a]); });
        }
        cartResp = await fetch('/cart/add.json', { method: 'POST', headers: cartHeaders, body: cartBody });
        if (cartResp.ok) break;

        cartErr = await cartResp.json().catch(function() { return {}; });
        var errMsg = (cartErr.description || cartErr.errors || '').toString();

        // First time we see a stale-cart "sold out", clear the stale items once
        // and retry immediately. Don't repeat the clear on every loop.
        if (!soldOutCleared && (errMsg.indexOf('sold out') !== -1 || errMsg.indexOf('stock') !== -1)) {
          soldOutCleared = true;
          try {
            var currentCart = await (await fetch('/cart.json')).json();
            var updates = {};
            (currentCart.items || []).forEach(function(item) {
              if (item.product_id === parseInt(productId)) updates[item.key] = 0;
            });
            if (Object.keys(updates).length > 0) {
              await fetch('/cart/update.json', {
                method: 'POST', headers: cartHeaders,
                body: JSON.stringify({ updates: updates })
              });
            }
          } catch (cleanupErr) {
            console.warn('Stale-cart cleanup failed:', cleanupErr);
          }
        }
        console.warn('Cart add attempt ' + (a + 1) + ' failed (' + cartResp.status + '): ' + errMsg);
      }

      if (!cartResp || !cartResp.ok) {
        console.error('Cart add failed after all retries:', cartResp && cartResp.status, cartErr);
        throw new Error('Add-to-cart failed. Try clearing your cart and retrying.');
      }

      // Redirect to cart
      window.location.href = '/cart';

    } catch (e) {
      console.error('Order error:', e);
      showError('Something went wrong. Please retry or contact contact@muralandco.com');
      setButtonLoading(false);
    }
  }

  function setButtonLoading(loading) {
    var text = document.getElementById('cfg-btn-text');
    var spinner = document.getElementById('cfg-btn-spinner');
    if (loading) {
      text.textContent = 'Creating…';
      spinner.style.display = 'inline-block';
      elButton.disabled = true;
    } else {
      text.textContent = 'Order my wallpaper';
      spinner.style.display = 'none';
      elButton.disabled = false;
    }
  }

  function showError(msg) {
    elError.textContent = msg;
    elError.style.display = 'block';
  }

  // --- Resize: reflow cropper to new container size ---
  // Debounced; on mobile we ignore pure-height changes (keyboard, URL-bar) by
  // only reacting when the viewport width actually changed.
  var resizeTimer = null;
  var lastViewportWidth = window.innerWidth;
  window.addEventListener('resize', function () {
    // Vertical-only changes (e.g. mobile URL bar show/hide, window height drag) don't
    // require a cropper rebuild — only react when the viewport width actually changes.
    if (window.innerWidth === lastViewportWidth) return;
    lastViewportWidth = window.innerWidth;
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      if (cropperInstance) updatePrice();
    }, 180);
  });

  // --- Init ---
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', loadProduct);
  } else {
    loadProduct();
  }
})();
